import React, { useMemo } from "react";
import { Table, Placeholder } from "react-bootstrap";
import { useVehicleAvailabilityCalendar } from "../../../../../api/fleet-management/dashboard/dashboard.hooks";

const toISODate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const getDatesBetween = (startISO, endISO) => {
  const start = new Date(startISO);
  const end = new Date(endISO);
  const dates = [];

  for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
    dates.push(toISODate(current));
  }

  return dates;
};

const safe = (value) => (value === null || value === undefined ? "-" : value);
const getTodayISO = () => toISODate(new Date());

const VehicleAvailabilityCalendar = ({ startDate, endDate }) => {
  const start = toISODate(startDate);
  const end = toISODate(endDate);

  const params = useMemo(() => ({ startDate: start, endDate: end }), [start, end]);
  const { data, isLoading, isError, error } = useVehicleAvailabilityCalendar(params);

  const { vehicles, dates } = useMemo(() => {
    const fallbackDates = getDatesBetween(start, end);
    const payload = data?.data || data;
    const vehiclesMap = new Map();

    if (payload && Array.isArray(payload.vehicles)) {
      payload.vehicles.forEach((vehicle) => {
        const vehicleId = vehicle.vehicleId || vehicle.vehicleId?._id || vehicle._id || vehicle.id;
        const label = vehicle.vehicleName || vehicle.vehicleSnapshot?.name || vehicle.snapshot?.name || `${vehicleId}`;
        const availability = new Map();

        (vehicle.availability || []).forEach((cell) => {
          const day = toISODate(cell.date || cell.day || cell);
          if (!day) return;
          availability.set(day, {
            totalStock: vehicle.totalStock ?? vehicle.stock ?? null,
            booked: cell.booked ?? null,
            available: cell.available ?? null,
          });
        });

        vehiclesMap.set(String(vehicleId), {
          label,
          availability,
          totalStock: vehicle.totalStock ?? null,
        });
      });
    }

    const normalizedVehicles = Array.from(vehiclesMap.entries()).map(([vehicleId, value]) => ({
      vehicleId,
      ...value,
    }));

    return {
      vehicles: normalizedVehicles,
      dates: payload?.dates || fallbackDates,
    };
  }, [data, start, end]);

  const todayISO = useMemo(() => getTodayISO(), []);
  const showSkeleton = isLoading && vehicles.length === 0;
  const rowCount = showSkeleton ? 3 : vehicles.length;

  const formatShortDate = (iso) => {
    try {
      const date = new Date(iso);
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit" }).format(date);
    } catch (err) {
      return iso;
    }
  };

  if (isError) {
    return (
      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">Vehicle Availability</h4>
        </div>
        <div className="card-body">
          <div className="text-danger mb-1">Error loading availability</div>
          <div className="small text-muted">{String(error?.message || error)}</div>
        </div>
      </div>
    );
  }

  if (!showSkeleton && vehicles.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">Vehicle Availability</h4>
        </div>
        <div className="card-body">
          <p className="text-muted mb-0 text-center">
            No vehicle availability data found for the selected range.
          </p>
        </div>
      </div>
    );
  }

  const renderCell = (day, vehicleIndex, rowIndex) => {
    const isToday = day === todayISO;
    const todayBackground = isToday ? { backgroundColor: "#eef8f2" } : undefined;

    if (showSkeleton) {
      return (
        <td
          key={`${vehicleIndex}-${rowIndex}-${day}`}
          style={{ minWidth: 108, width: 108, textAlign: "center", verticalAlign: "middle", ...todayBackground }}
        >
        </td>
      );
    }

    const vehicle = vehicles[vehicleIndex];
    const cell = vehicle.availability.get(day) || {};

    return (
      <td
        key={`${vehicle.vehicleId}-${day}`}
        style={{ minWidth: 108, width: 108, textAlign: "center", verticalAlign: "middle", ...todayBackground }}
      >
        <div className="small text-muted">{cell.totalStock != null ? cell.totalStock : "-"}</div>
        <div className="small text-danger">Booked: {safe(cell.booked)}</div>
        <div className="small text-success">Available: {safe(cell.available)}</div>
      </td>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="card-title mb-0">Vehicle Availability</h4>
      </div>

      <div className="card-body">
        <div className="table-responsive">
          <Table bordered className="table align-middle mb-0" style={{ minWidth: Math.max(900, 260 + dates.length * 108) }}>
          <thead>
            <tr>
              <th className="position-sticky start-0 top-0 bg-body-tertiary" style={{ minWidth: 260, width: 260, left: 0, zIndex: 4 }}>
                Vehicle / Type
              </th>
              {dates.map((day) => (
                <th
                  key={day}
                  style={{
                    minWidth: 108,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    backgroundColor: day === todayISO ? "#eaf5ee" : undefined,
                  }}
                >
                  {formatShortDate(day)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {Array.from({ length: rowCount }).map((_, rowIndex) => {
              const vehicle = vehicles[rowIndex];
              const rowKey = vehicle?.vehicleId || `skeleton-${rowIndex}`;

              return (
                <tr key={rowKey}>
                  <td className="position-sticky start-0 bg-white" style={{ minWidth: 260, width: 260, left: 0, zIndex: 3 }}>
                    {showSkeleton ? (
                      <>
                        <Placeholder as="div" animation="glow" className="mb-2">
                          <Placeholder xs={8} />
                        </Placeholder>
                        <Placeholder as="div" animation="glow">
                          <Placeholder xs={4} />
                        </Placeholder>
                      </>
                    ) : (
                      <>
                        <div className="fw-bold">{vehicle.label}</div>
                        {vehicle.totalStock != null && (
                          <div className="small text-muted">Stock: {vehicle.totalStock}</div>
                        )}
                      </>
                    )}
                  </td>

                  {dates.map((day) => renderCell(day, rowIndex, rowIndex))}
                </tr>
              );
            })}
          </tbody>
        </Table>
        </div>
      </div>
    </div>
  );
};

export default VehicleAvailabilityCalendar;